import React from 'react';
import { Document, View } from '@react-pdf/renderer';
import type { ModuleId, ReportData } from '../tokens/types';
import { MODULES, MODULE_META_EXTRA } from '../modules/moduleRegistry';
import { PdfCover, PdfPage } from '../primitives/PdfPrimitives';
import { PdfDisclaimer } from '../primitives/DisclaimerPage';
import { PdfToc, type TocEntry } from '../primitives/TocPage';
import { RenderTargetProvider } from '../primitives/RenderTarget';
import { PdfDirectorDetail } from '../modules/Management';
import { fmtDate } from '../modules/formatUtils';
import '../tokens/pdfFonts';

// Rendered-height ceiling per module (pt) used only for flow-mode packing.
// A4 usable content is 684pt after margin tightening. Estimates sit near the
// 75th-percentile observed render height (NOT the median) so packing stays
// conservative and modules don't overflow into the footer area.
//
// If a module's content exceeds its estimate, react-pdf's `wrap` will move the
// overflow to the next page — but only at safe child boundaries. Underestimating
// here was the root cause of cross-section bleed, so values err high.
const MODULE_PDF_HEIGHT: Partial<Record<ModuleId, number>> = {
    identity: 200,
    score: 340,
    riskParameters: 420,
    overview: 780,
    // 2-col card grid (~152pt each row) + employees block (~140pt). 4 dirs = ~440pt;
    // bump above that so packIntoPages doesn't squeeze anything else onto the page.
    management: 540,
    directorsDeep: 720,
    relatedParties: 360,
    funding: 280,
    mca: 160,
    mcaCharges: 320,
    compliances: 320,
    financialMetrics: 340,
    auditedStatements: 540,
    bankingCashflow: 260,
    legalRisk: 300,
    alerts: 240,
};
// 842pt (A4 height) − 79pt (paddingTop) − 79pt (paddingBottom) = 684pt usable.
// Conservative ceiling — packIntoPages stops adding before this is hit so a module
// that under-estimates its height by up to ~30pt still won't bleed into the footer.
const PAGE_CONTENT_HEIGHT = 660;
const MODULE_GAP = 14;

type RenderableModule = (typeof MODULES)[keyof typeof MODULES];

const packIntoPages = (modules: RenderableModule[]): RenderableModule[][] => {
    const pages: RenderableModule[][] = [];
    let current: RenderableModule[] = [];
    let currentHeight = 0;
    for (const m of modules) {
        const h = MODULE_PDF_HEIGHT[m.meta.id] ?? 280;
        if (current.length === 0) {
            current.push(m);
            currentHeight = h;
            continue;
        }
        if (currentHeight + MODULE_GAP + h > PAGE_CONTENT_HEIGHT) {
            pages.push(current);
            current = [m];
            currentHeight = h;
        } else {
            current.push(m);
            currentHeight += MODULE_GAP + h;
        }
    }
    if (current.length) pages.push(current);
    return pages;
};

export type ReportDocumentProps = {
    data: ReportData;
    enabledModules: ModuleId[];
    generatedOn?: string;
};

export const ReportDocument: React.FC<ReportDocumentProps> = ({ data, enabledModules, generatedOn }) => {
    const today = generatedOn ?? fmtDate(new Date().toISOString());
    const meta: Array<{ label: string; value: string }> = [];
    if (data.identity.cin && data.identity.cin !== '—') meta.push({ label: 'CIN', value: data.identity.cin });
    if (data.identity.pan && data.identity.pan !== '—') meta.push({ label: 'PAN', value: data.identity.pan });
    if (data.identity.sector && data.identity.sector !== '—') meta.push({ label: 'Sector', value: data.identity.sector });
    if (data.identity.state) meta.push({ label: 'HQ', value: data.identity.state });
    if (data.identity.foundationDate) meta.push({ label: 'Founded', value: data.identity.foundationDate });
    if (data.score?.companyScore != null) meta.push({ label: 'AICA Score', value: `${data.score.companyScore.toFixed(1)} / 4` });

    const renderable = enabledModules.map((id) => MODULES[id as keyof typeof MODULES]).filter((m) => m && m.hasData(data));

    const tocEntries: TocEntry[] = renderable.map((m, i) => ({
        index: String(i + 1).padStart(2, '0'),
        title: m.meta.title,
        subtitle: m.meta.description,
        coverage: MODULE_META_EXTRA[m.meta.id]?.coverage,
    }));

    return (
        <Document
            title={`AICA Credit Report — ${data.identity.orgName}`}
            author="Recur Club"
            subject={`Credit intelligence report for ${data.identity.orgName}`}
        >
            <PdfCover
                orgName={data.identity.orgName}
                subtitle={[data.identity.sector, data.identity.state].filter(Boolean).join(' · ')}
                meta={meta}
                generatedOn={today}
            />
            {tocEntries.length > 0 ? <PdfToc orgName={data.identity.orgName} entries={tocEntries} /> : null}
            {packIntoPages(renderable).map((group, gi) => {
                const sectionLabel =
                    group.length === 1
                        ? `${String(renderable.indexOf(group[0]) + 1).padStart(2, '0')} · ${group[0].meta.title}`
                        : `${String(renderable.indexOf(group[0]) + 1).padStart(2, '0')}–${String(
                              renderable.indexOf(group[group.length - 1]) + 1,
                          ).padStart(2, '0')}`;

                // If this group contains the management module, emit one extra
                // PdfPage per director RIGHT AFTER it. Director detail pages
                // share the management module's TOC index (e.g. "05") in their
                // chrome label, since they belong to the same section.
                const mgmtModule = group.find((m) => m.meta.id === 'management');
                const directors = mgmtModule && data.management ? data.management.profiles : [];
                const mgmtIndex = mgmtModule ? String(renderable.indexOf(mgmtModule) + 1).padStart(2, '0') : '';

                return (
                    <React.Fragment key={`flow-${gi}`}>
                        <PdfPage orgName={data.identity.orgName} sectionLabel={sectionLabel}>
                            {group.map((m, i) => {
                                const Mod: any = m.Pdf;
                                const globalIdx = renderable.indexOf(m);
                                const index = String(globalIdx + 1).padStart(2, '0');
                                return (
                                    <View key={m.meta.id} style={{ marginBottom: i < group.length - 1 ? 18 : 0 }}>
                                        <RenderTargetProvider target="pdf">
                                            <Mod data={data} index={index} breakBefore={false} />
                                        </RenderTargetProvider>
                                    </View>
                                );
                            })}
                        </PdfPage>
                        {directors.map((profile, di) => (
                            <PdfPage key={`director-${di}`} orgName={data.identity.orgName} sectionLabel={`${mgmtIndex} · Director Detail`}>
                                <RenderTargetProvider target="pdf">
                                    <PdfDirectorDetail
                                        profile={profile}
                                        moduleIndex={mgmtIndex}
                                        directorIndex={di + 1}
                                        directorTotal={directors.length}
                                        data={data}
                                    />
                                </RenderTargetProvider>
                            </PdfPage>
                        ))}
                    </React.Fragment>
                );
            })}
            <PdfPage orgName={data.identity.orgName} sectionLabel="Disclaimer">
                <PdfDisclaimer orgName={data.identity.orgName} />
            </PdfPage>
        </Document>
    );
};
